/**
 * TerminalPanel - Main terminal output and input area
 * Connects to VoxTerm voice/text processing via WebSocket
 * Supports both text input and voice input with real-time audio streaming
 */

import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import NoiseDropdown from './NoiseDropdown';
import { getUserPreferences } from '../services/api';
import type { NoiseReductionType } from '../types/user';

// Exposed methods for parent component
export interface TerminalPanelHandle {
  playAudioChunk: (base64Audio: string) => Promise<void>;
}

interface Terminal {
  id: string;
  name: string;
  current_disk: string | null;
  current_cwd: string;
  history: Array<{ command: string; result: any; timestamp: string }>;
}

interface BrainStatus {
  state: string;
  exists: boolean;
  brain_id?: string;
  relay_connected?: boolean;
}

interface Transcript {
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
}

interface ToolExecution {
  name: string;
  arguments?: Record<string, any>;
  result?: any;
}

// Recording states
type RecordingState = 'idle' | 'listening' | 'speaking';

interface TerminalPanelProps {
  terminal: Terminal;
  brainStatus: BrainStatus;
  onStartBrain: () => void;
  voiceConnected: boolean;
  transcripts: Transcript[];
  currentTool: ToolExecution | null;
  onSendText: (text: string) => void;
  onSendAudio: (base64Audio: string) => void;
  onSendCommit: () => void;
  wsRef: React.RefObject<WebSocket | null>;
}

const TerminalPanel = forwardRef<TerminalPanelHandle, TerminalPanelProps>(({
  terminal,
  brainStatus,
  onStartBrain,
  voiceConnected,
  transcripts,
  currentTool,
  onSendText,
  onSendAudio,
  onSendCommit,
  wsRef,
}, ref) => {
  const [input, setInput] = useState('');
  const outputRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Voice recording state
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const recordingStateRef = useRef<RecordingState>('idle');

  // Noise reduction preference
  const [noiseReduction, setNoiseReduction] = useState<NoiseReductionType>('far_field');

  // Audio capture refs
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  // Audio playback refs
  const audioQueueRef = useRef<AudioBuffer[]>([]);
  const isPlayingRef = useRef<boolean>(false);
  const nextPlayTimeRef = useRef<number>(0);
  const currentAudioSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const isInterruptedRef = useRef<boolean>(false);

  // Update recording state and ref together
  const updateRecordingState = useCallback((state: RecordingState) => {
    console.log(`Recording state: ${recordingStateRef.current} -> ${state}`);
    setRecordingState(state);
    recordingStateRef.current = state;
  }, []);

  // Load noise reduction preference on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await getUserPreferences();
        setNoiseReduction(prefs.noise_reduction);
      } catch (error) {
        console.error('Failed to load preferences:', error);
      }
    };
    loadPreferences();
  }, []);

  // Auto-scroll to bottom when content changes
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [terminal.history, transcripts, currentTool]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, [terminal.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording(false);
      cleanupAudioPlayback();
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Send text to GPT-Realtime via WebSocket
    onSendText(input);
    setInput('');
  };

  const formatResult = (result: any): string => {
    if (typeof result === 'string') return result;
    if (typeof result === 'object') return JSON.stringify(result, null, 2);
    return String(result);
  };

  // =====================
  // Audio Recording
  // =====================

  const startRecording = async (): Promise<void> => {
    if (!voiceConnected) {
      console.warn('Cannot start recording - not connected');
      return;
    }

    console.log('Starting recording...');
    isInterruptedRef.current = false;
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextPlayTimeRef.current = 0;

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });

      mediaStreamRef.current = stream;

      // Create or reuse AudioContext
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      }

      const audioContext = audioContextRef.current;

      // Resume if suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Create source from microphone
      const source = audioContext.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      // Cleanup old processor
      if (processorNodeRef.current) {
        processorNodeRef.current.disconnect();
        processorNodeRef.current.onaudioprocess = null;
        processorNodeRef.current = null;
      }

      // Cleanup old gain node
      if (gainNodeRef.current) {
        gainNodeRef.current.disconnect();
        gainNodeRef.current = null;
      }

      // Create ScriptProcessorNode for real-time audio processing
      // Buffer size 4096 gives us chunks every ~170ms at 24kHz
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorNodeRef.current = processor;

      processor.onaudioprocess = (event) => {
        const inputData = event.inputBuffer.getChannelData(0);

        // Convert Float32 to PCM16
        const pcm16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }

        // Convert to base64
        const uint8Array = new Uint8Array(pcm16.buffer);
        const binaryString = String.fromCharCode(...uint8Array);
        const base64Audio = btoa(binaryString);

        // Send to server
        onSendAudio(base64Audio);
      };

      // ScriptProcessorNode requires connection to destination
      // Mute with GainNode to prevent feedback
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0;
      gainNodeRef.current = gainNode;

      // Connect: source -> processor -> gainNode(muted) -> destination
      source.connect(processor);
      processor.connect(gainNode);
      gainNode.connect(audioContext.destination);

      console.log('Recording started');
      updateRecordingState('listening');
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Failed to access microphone. Please check permissions.');
    }
  };

  const stopRecording = (sendCommitOnStop: boolean = false): void => {
    console.log(`Stopping recording (commit: ${sendCommitOnStop})`);

    // Disconnect processor node
    if (processorNodeRef.current) {
      processorNodeRef.current.disconnect();
      processorNodeRef.current.onaudioprocess = null;
      processorNodeRef.current = null;
    }

    // Disconnect gain node
    if (gainNodeRef.current) {
      gainNodeRef.current.disconnect();
      gainNodeRef.current = null;
    }

    // Disconnect source node
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }

    // Stop microphone stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Send commit if requested
    if (sendCommitOnStop) {
      onSendCommit();
    }

    updateRecordingState('idle');
  };

  const toggleRecording = (): void => {
    const currentState = recordingStateRef.current;
    console.log(`Toggle recording - current state: ${currentState}`);

    if (currentState === 'listening') {
      // Stop recording (no commit - OpenAI handles VAD)
      stopRecording(false);
    } else if (currentState === 'speaking') {
      // Stop AI speaking
      stopAISpeech();
    } else if (currentState === 'idle') {
      // Start recording
      startRecording();
    }
  };

  // =====================
  // Audio Playback
  // =====================

  const cleanupAudioPlayback = (): void => {
    currentAudioSourcesRef.current.forEach(source => {
      try {
        source.stop();
        source.disconnect();
      } catch (e) {
        // Source might have already stopped
      }
    });
    currentAudioSourcesRef.current = [];
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextPlayTimeRef.current = 0;
  };

  const stopAISpeech = (): void => {
    console.log('Stopping AI speech');
    cleanupAudioPlayback();
    isInterruptedRef.current = true;
    updateRecordingState('idle');
  };

  // Expose playAudioChunk for parent to call
  const playAudioChunk = useCallback(async (base64Audio: string): Promise<void> => {
    try {
      if (isInterruptedRef.current) {
        return;
      }

      // Create AudioContext if needed
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      }

      const audioContext = audioContextRef.current;

      // Resume if suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Decode base64 to binary
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert PCM16 to Float32
      const pcm16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768.0;
      }

      // Create audio buffer
      const audioBuffer = audioContext.createBuffer(1, float32.length, 24000);
      audioBuffer.getChannelData(0).set(float32);

      // Add to queue
      audioQueueRef.current.push(audioBuffer);

      // Start playing if not already
      if (!isPlayingRef.current) {
        playNextAudioChunk();
      }
    } catch (error) {
      console.error('Failed to play audio:', error);
    }
  }, []);

  const playNextAudioChunk = (): void => {
    if (!audioContextRef.current) return;

    // Check if queue is empty and all sources finished
    if (audioQueueRef.current.length === 0 && currentAudioSourcesRef.current.length === 0) {
      console.log('Audio playback complete');
      isPlayingRef.current = false;
      isInterruptedRef.current = false;
      nextPlayTimeRef.current = 0;
      updateRecordingState('idle');
      return;
    }

    // Update state when AI starts speaking
    if (!isPlayingRef.current && recordingStateRef.current !== 'speaking') {
      updateRecordingState('speaking');
    }

    isPlayingRef.current = true;
    const audioContext = audioContextRef.current;

    // Schedule all queued chunks for gapless playback
    while (audioQueueRef.current.length > 0) {
      const audioBuffer = audioQueueRef.current.shift()!;
      const currentTime = audioContext.currentTime;

      // Calculate start time
      let startTime;
      if (nextPlayTimeRef.current === 0 || nextPlayTimeRef.current < currentTime) {
        startTime = currentTime;
      } else {
        startTime = nextPlayTimeRef.current;
      }

      // Create and schedule audio source
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);

      // Track for cleanup
      currentAudioSourcesRef.current.push(source);

      // Remove from tracking when done
      source.onended = () => {
        const index = currentAudioSourcesRef.current.indexOf(source);
        if (index > -1) {
          currentAudioSourcesRef.current.splice(index, 1);
        }
        // Check if more to play
        if (audioQueueRef.current.length > 0 || currentAudioSourcesRef.current.length > 0) {
          playNextAudioChunk();
        } else {
          // All done
          isPlayingRef.current = false;
          isInterruptedRef.current = false;
          nextPlayTimeRef.current = 0;
          updateRecordingState('idle');
        }
      };

      // Schedule playback
      source.start(startTime);
      nextPlayTimeRef.current = startTime + audioBuffer.duration;
    }
  };

  // Expose playAudioChunk to parent via imperative handle
  useImperativeHandle(ref, () => ({
    playAudioChunk
  }), [playAudioChunk]);

  // Get button styles based on recording state
  const getMicButtonStyles = (): string => {
    if (!voiceConnected) {
      return 'bg-gray-600 opacity-50 cursor-not-allowed';
    }
    switch (recordingState) {
      case 'listening':
        return 'bg-red-500 hover:bg-red-600 animate-pulse';
      case 'speaking':
        return 'bg-purple-600 hover:bg-purple-700';
      default:
        return 'bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90';
    }
  };

  const getMicButtonTitle = (): string => {
    if (!voiceConnected) return 'Connecting to voice service...';
    switch (recordingState) {
      case 'listening':
        return 'Recording... Click to stop';
      case 'speaking':
        return 'AI speaking... Click to interrupt';
      default:
        return 'Click to start voice input';
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-gray-900">
      {/* Connection status bar */}
      {(!voiceConnected || brainStatus.state !== 'active') && (
        <div className="bg-yellow-900/30 border-b border-yellow-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-yellow-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span>
              {!voiceConnected
                ? 'Connecting to voice service...'
                : `Brain is ${brainStatus.state}. Waiting for connection...`}
            </span>
          </div>
          <span className="text-yellow-400 text-sm animate-pulse">
            Connecting...
          </span>
        </div>
      )}

      {/* Terminal Output - scrollable area */}
      <div
        ref={outputRef}
        className="flex-1 overflow-y-auto p-4 pb-6 font-mono text-sm"
        onClick={() => inputRef.current?.focus()}
      >
        {/* Welcome message */}
        {transcripts.length === 0 && terminal.history.length === 0 && (
          <div className="text-gray-500 mb-4">
            <p>Welcome to VoxTerm - Voice-controlled Voxtron Terminal</p>
            <p>Type commands in natural language or speak to interact with your disks.</p>
            <p className="mt-2">
              Try: <span className="text-green-400">"list my disks"</span> or{' '}
              <span className="text-green-400">"create a disk called notes"</span>
            </p>
          </div>
        )}

        {/* Conversation Transcripts */}
        {transcripts.map((transcript, index) => (
          <div key={`transcript-${index}`} className="mb-3">
            {transcript.role === 'user' ? (
              <div className="flex items-start gap-2">
                <span className="text-blue-400 font-semibold">You:</span>
                <span className="text-white">{transcript.text}</span>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <span className="text-purple-400 font-semibold">VoxTerm:</span>
                <span className="text-gray-200">{transcript.text}</span>
              </div>
            )}
          </div>
        ))}

        {/* Current Tool Execution */}
        {currentTool && (
          <div className="mb-3 border-l-2 border-yellow-500 pl-3">
            <div className="flex items-center gap-2 text-yellow-400">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Executing: {currentTool.name}</span>
            </div>
            {currentTool.arguments && (
              <pre className="text-xs text-gray-500 mt-1">
                {JSON.stringify(currentTool.arguments, null, 2)}
              </pre>
            )}
            {currentTool.result && (
              <div className="mt-2 text-gray-300">
                <span className="text-green-400">Result: </span>
                <pre className="whitespace-pre-wrap break-words text-xs">
                  {formatResult(currentTool.result)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Listening indicator */}
        {recordingState === 'listening' && (
          <div className="flex items-center gap-2 text-red-400">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span>Listening...</span>
          </div>
        )}

        {/* Speaking indicator */}
        {recordingState === 'speaking' && (
          <div className="flex items-center gap-2 text-purple-400">
            <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
            <span>VoxTerm is speaking...</span>
          </div>
        )}

        {/* Waiting indicator when connected but no transcripts yet */}
        {voiceConnected && transcripts.length === 0 && recordingState === 'idle' && (
          <div className="flex items-center gap-2 text-gray-500">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span>Connected - type a message or click the mic to speak</span>
          </div>
        )}
      </div>

      {/* Input Area - sticky at bottom */}
      <form
        onSubmit={handleSubmit}
        className="flex-shrink-0 border-t border-gray-700 p-4 bg-gray-800"
      >
        <div className="flex items-center gap-3">
          {/* Text Input */}
          <div className="flex-1 flex items-center gap-2 bg-gray-900 rounded-lg px-4 py-2 border border-gray-600 focus-within:border-blue-500">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={voiceConnected ? "Type a message or command..." : "Connecting..."}
              className="flex-1 bg-transparent text-white font-mono text-sm focus:outline-none placeholder:text-gray-500"
              disabled={!voiceConnected}
            />
          </div>

          {/* Noise Dropdown */}
          <NoiseDropdown
            value={noiseReduction}
            onChange={setNoiseReduction}
            disabled={recordingState !== 'idle'}
          />

          {/* Voice Button */}
          <button
            type="button"
            onClick={toggleRecording}
            className={`w-12 h-12 rounded-full text-white flex items-center justify-center transition-all ${getMicButtonStyles()}`}
            disabled={!voiceConnected}
            title={getMicButtonTitle()}
          >
            {recordingState === 'listening' ? (
              // Stop icon when listening
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="1" />
              </svg>
            ) : recordingState === 'speaking' ? (
              // Pause/stop icon when speaking
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              // Microphone icon when idle
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                />
              </svg>
            )}
          </button>
        </div>
      </form>
    </div>
  );
});

TerminalPanel.displayName = 'TerminalPanel';

export default TerminalPanel;

// Export type for parent reference
export type { RecordingState };
